import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Anchor, TableData, Typography } from 'components/base';
import Icon, { ICON_TYPE } from 'components/icon';
import useModalContext from 'components/context/modal';
import { formatDate, parseDate } from 'utils/format';
import { useApiHandler } from '../../lib/useApi';
import usersApi from '../../api/users';
import SignInWithAmazon from '../signInWithAmazon';
import SignInWithFacebook from '../signInWithFacebook';
import SignInWithAncestry from '../signInWithAncestry';
import withFacebook from '../../hoc/withFacebook';
import withAmazon from '../../hoc/withAmazon';
import SignInWithFamilySearch from '../signInWithFamilySearch';

export const PROVIDER = {
  EMAIL: 0,
  INSTAGRAM: 3,
  AMAZON: 4,
  FACEBOOK: 5,
  ANCESTRY: 6,
  FAMILY_SEARCH: 7,
};

const PROVIDER_ICON = {
  [PROVIDER.EMAIL]: (
    <Icon
      type={ICON_TYPE.ENVELOPE}
      className="text-secondary"
      size={16}
      style={{ marginBottom: 3 }}
    />
  ),
  [PROVIDER.INSTAGRAM]: (
    <Icon
      type={ICON_TYPE.INSTAGRAM}
      className="text-primary"
      size={16}
      style={{ marginBottom: 4 }}
    />
  ),
  [PROVIDER.AMAZON]: <Icon type={ICON_TYPE.AMAZON} className="text-amazon" size={16} />,
  [PROVIDER.FACEBOOK]: (
    <Icon
      type={ICON_TYPE.FACEBOOK}
      className="text-facebook"
      size={16}
      style={{ marginBottom: 3 }}
    />
  ),
  [PROVIDER.ANCESTRY]: <Icon type={ICON_TYPE.ANCESTRY} className="text-ancestry" size={16} />,
  [PROVIDER.FAMILY_SEARCH]: <Icon type={ICON_TYPE.FAMILY_SEARCH} className="text-familysearch" size={16} />,
};
const PROVIDER_LABEL = {
  [PROVIDER.EMAIL]: 'E-mail',
  [PROVIDER.INSTAGRAM]: 'Instagram',
  [PROVIDER.AMAZON]: 'Amazon',
  [PROVIDER.FACEBOOK]: 'Facebook',
  [PROVIDER.ANCESTRY]: 'Ancestry',
  [PROVIDER.FAMILY_SEARCH]: 'FamilySearch',
};
const PROVIDER_CHILDREN = {
  [PROVIDER.AMAZON]: withAmazon(props => {
    const onSuccess = async amazonUserData => {
      const { error } = await usersApi(props.accessToken).addProvider({
        provider: 4,
        user_id: amazonUserData.id,
        token: amazonUserData.token,
        email: amazonUserData.email,
      });

      props.onUpdate();

      if (error) {
        props.toast(error, { type: 'warning' });
      } else {
        props.toast('Your Amazon account was successfully connected!', { type: 'success' });
      }
    };

    return <SignInWithAmazon redirectUrl="/user" onSuccess={onSuccess} />;
  }),
  [PROVIDER.FACEBOOK]: withFacebook(props => {
    const onSuccess = async facebookUserData => {
      const { error } = await usersApi(props.accessToken).addProvider({
        provider: 5,
        user_id: facebookUserData.id,
        name: facebookUserData.name,
        email: facebookUserData.email,
      });

      props.onUpdate();

      if (error) {
        props.toast(error, { type: 'warning' });
      } else {
        props.toast('Your Facebook account was successfully connected!', { type: 'success' });
      }
    };

    return <SignInWithFacebook fb={props.fb} onSuccess={onSuccess} requiredEmail />;
  }),
  [PROVIDER.ANCESTRY]: props => {
    return <SignInWithAncestry redirectUrl="referer" />;
  },
  [PROVIDER.FAMILY_SEARCH]: props => {
    return <SignInWithFamilySearch redirectUrl="referer" />;
  },
};

const UserAuthProviders = props => {
  const modalContext = useModalContext();

  const providers = useMemo(() => {
    return [...props.providers].sort((a, b) => {
      return a.created_at > b.created_at ? 1 : -1;
    });
  }, [props.providers]);

  const enableDelete = useMemo(() => {
    return props.accessToken && providers.length >= 2;
  }, [providers, props.accessToken]);

  const offerAddProviders = useMemo(() => {
    if (props.accessToken) {
      return props.availableProviders.filter(provider => {
        return !providers.find(p => p.type === provider);
      });
    }

    return [];
  }, [providers, props.availableProviders, props.accessToken]);

  const [toggle, setToggle] = useState({});

  const handleOnRemoveProviderClick = useApiHandler(async (provider, accessToken) => {
    modalContext.confirm('Do you really want to remove your connected account?', {
      title: 'Remove connected account?',
      onConfirm: async () => {
        const { error } = await usersApi(accessToken).removeProvider(
          provider.type,
          provider.remote_id,
        );

        props.onReload();

        if (error) {
          modalContext.toast('Oops, something went wrong. Please try again.', { type: 'warning' });
        } else {
          modalContext.toast('Your connected account was successfully removed.', {
            type: 'success',
          });
        }
      },
    });
  });

  return (
    <div className={props.className}>
      <Typography.H3 className="mt-5 mb-2">Connected Accounts</Typography.H3>
      <TableData.Wrapper type="providers" appearance="compact">
        <TableData.Row type="header">
          <TableData.Col type="status">Provider</TableData.Col>
          <TableData.Col type="date">Identifier</TableData.Col>
          <TableData.Col type="status">Registered</TableData.Col>
          <TableData.Col type="order-id" />
        </TableData.Row>
        {providers.map(provider => {
          return (
            <React.Fragment key={provider.remote_id}>
              <TableData.Row>
                <TableData.Col type="status">
                  {PROVIDER_ICON[provider.type] ?? null} {PROVIDER_LABEL[provider.type]}
                </TableData.Col>
                <TableData.Col type="date">{provider.email || provider.remote_id}</TableData.Col>
                <TableData.Col type="status">
                  {provider.created_at &&
                    formatDate(parseDate(provider.created_at), 'MMM Do, YYYY')}
                </TableData.Col>
                <TableData.Col type="order-id text-right">
                  {enableDelete && (
                    <Anchor
                      className="text-danger"
                      onClick={e => {
                        e.preventDefault();
                        handleOnRemoveProviderClick(provider);
                      }}
                    >
                      Remove
                    </Anchor>
                  )}
                </TableData.Col>
              </TableData.Row>
            </React.Fragment>
          );
        })}
        {offerAddProviders.map(provider => {
          const Component = PROVIDER_CHILDREN[provider];
          return (
            <React.Fragment key={provider}>
              <TableData.Row>
                <TableData.Col>
                  {PROVIDER_ICON[provider] ?? null}{' '}
                  <Anchor
                    className="text-primary"
                    onClick={e => {
                      e.preventDefault();
                      setToggle(val => ({ [provider]: !val[provider] }));
                    }}
                  >
                    Add {PROVIDER_LABEL[provider]} Account
                  </Anchor>
                </TableData.Col>
              </TableData.Row>
              <TableData.Row className={toggle[provider] ? 'w-100' : 'd-none'}>
                <div className="w-100 text-center">
                  <Component
                    accessToken={props.accessToken}
                    onUpdate={props.onReload}
                    toast={modalContext.toast}
                  />
                </div>
              </TableData.Row>
            </React.Fragment>
          );
        })}
      </TableData.Wrapper>
    </div>
  );
};

UserAuthProviders.propTypes = {
  accessToken: PropTypes.string,
  availableProviders: PropTypes.array,
  providers: PropTypes.array,
  className: PropTypes.string,
  children: PropTypes.node,
  onReload: PropTypes.func,
};

UserAuthProviders.defaultProps = {
  onReload: () => { },
  availableProviders: [PROVIDER.AMAZON, PROVIDER.FACEBOOK],
};

export default UserAuthProviders;
